/**
 * Instalación de la app en el teléfono.
 *
 * **Por qué hay que capturar el evento y no dejar que el navegador se arregle
 * solo.** Chrome dispara `beforeinstallprompt` una vez y, si nadie lo frena,
 * muestra su propio cartelito de instalación. Ese cartel aparece **una sola
 * vez**: si la persona lo cierra —y en un teléfono, con un dedo grande y un
 * cartel chico, se cierra sin querer— el navegador no lo vuelve a ofrecer, y
 * desde la app no queda ninguna forma de proponerlo de nuevo. Guardando el
 * evento podemos ofrecer "Instalar" en el momento que tenga sentido, todas las
 * veces que haga falta.
 *
 * **Safari no dispara este evento nunca.** En iPhone la instalación es a mano
 * (Compartir → Agregar a inicio) y no hay API que la dispare, así que ahí lo
 * único que se puede hacer es explicarla. Por eso `esIOS()` existe: no es
 * detección de navegador por gusto, es la diferencia entre mostrar un botón
 * que funciona y mostrar uno que no hace nada.
 */

/** Evento de instalación guardado, o `null` si el navegador no lo ofreció. */
let eventoDiferido = null;

const suscriptores = new Set();

function notificar() {
  for (const fn of suscriptores) {
    try { fn(); } catch { /* un suscriptor roto no puede frenar al resto */ }
  }
}

/** Avisa a la UI cuando cambia la posibilidad de instalar. */
export function suscribirse(fn) {
  suscriptores.add(fn);
  return () => suscriptores.delete(fn);
}

/** True si el navegador ya nos dejó el evento y podemos ofrecer el botón. */
export function sePuedeInstalar() {
  return eventoDiferido !== null;
}

/**
 * True si la app ya está corriendo instalada (ícono propio, sin barra de
 * direcciones). Sirve para no ofrecer instalar algo que ya está instalado.
 *
 * `navigator.standalone` es la variante de Safari en iOS, que no implementa
 * `display-mode: standalone` en todas las versiones.
 */
export function estaInstalada() {
  if (typeof window === 'undefined') return false;
  const porDisplayMode = window.matchMedia?.('(display-mode: standalone)')?.matches;
  return Boolean(porDisplayMode || window.navigator?.standalone);
}

/** True en iPhone/iPad, donde la instalación se hace a mano desde Safari. */
export function esIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // El iPad moderno se declara "Macintosh"; lo que lo delata es que la pantalla
  // responde al tacto, cosa que ninguna Mac hace.
  const esIpadModerno = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || esIpadModerno;
}

/**
 * Lanza el diálogo de instalación del navegador.
 *
 * Devuelve `'aceptada'`, `'rechazada'` o `'no-disponible'`. El evento se
 * consume al usarlo —no se puede volver a llamar sobre el mismo—, así que se
 * descarta pase lo que pase. Si la persona dijo que no, Chrome vuelve a
 * ofrecerlo en visitas posteriores y lo capturamos de nuevo.
 */
export async function instalar() {
  if (!eventoDiferido) return 'no-disponible';

  const evento = eventoDiferido;
  eventoDiferido = null;
  notificar();

  try {
    evento.prompt();
    const { outcome } = await evento.userChoice;
    return outcome === 'accepted' ? 'aceptada' : 'rechazada';
  } catch {
    return 'no-disponible';
  }
}

// ─── Enganche con el navegador ────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (evento) => {
    // Frena el cartel propio del navegador para poder ofrecerlo nosotros, en
    // la pantalla que lo explica y tantas veces como haga falta.
    evento.preventDefault();
    eventoDiferido = evento;
    notificar();
  });

  window.addEventListener('appinstalled', () => {
    eventoDiferido = null;
    notificar();
  });
}
